<template>
    <div>
        <b-alert :show="!!errorHandlingMessage" variant="danger" dismissible>{{ errorHandlingMessage }}</b-alert>
        <step-selectors
            :steps="stepsWithTasks"
            :loading="showSelectorsLoading"
            value-field="state_name"
            :selected="selectedStepName"
            @selected="handleStepSelection"
        ></step-selectors>
        <!-- <div>
            <b-link @click="reload">Reload</b-link>
        </div> -->

        <transition name="fade-fast" mode="out-in">

            <div v-if="loading" key="isLoading" class="min-height-300 d-flex justify-content-center align-items-center">
                <busy-spinner text="Loading tasks..."></busy-spinner>
            </div>

            <div v-else key="notLoading" class="mb-5">  <!-- TASK AREA - don't show if loading everything -->
                <workflow-task
                    v-for="task in selectedStepTasks"
                    :key="task.id"
                    :task-id="task.id"
                    :task="task"
                    class="mt-4 border rounded-lg bg-gray-50 shadow-sm spl-animate spl-animate-fade-down"
                    @state-changed="handleTaskStateChanged"
                ></workflow-task>

                <div v-if="selectedStep && selectedStep.state_name === 'closed'" class="">
                    <div 
                        v-if="selectedStep && selectedStep.isCurrentAndFinal"
                        key="isClosed"
                        class="mt-4 d-flex flex-column gap-4 align-items-center justify-content-center min-height-300 spl-animate spl-animate-fade-down"
                    >
                        <div class="text-center border rounded-lg shadow-sm p-4 bg-white overflow-hidden min-width-300">
                            <h3>
                                {{ workflow_spec.name }} Closed
                            </h3>
                            <p class="text-muted mb-0">
                                {{ closedNote }}
                            </p>
                        </div>
                        <!-- Add something like this back in, but should not be hard-coded to the quality list -->
                        <!-- <b-link href="/quality/">&larr; Back to Quality Issues List</b-link> -->
                    </div>

                    <div 
                        v-else
                        key="notClosed"
                        class="mt-4 d-flex align-items-center justify-content-center min-height-300 spl-animate spl-animate-fade-down"
                    >
                        <div class="text-center">
                            <p class="text-muted font-weight-bold font-size-100 mb-0">
                                This workflow is still open.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </transition>

        <!-- <fishbone-diagram
            title="Fishbone Component"
            subtitle="Possible causes of the issue"
        ></fishbone-diagram> -->

        <!-- <div v-if="d3Svg">
            {{ d3Svg }}
        </div> -->

    </div>
</template>

<script>
import { suppliosApi } from '../../js/api-service';
import { errorHandling, currentUserInfoMixin } from '../../js/custom-vue-mixins';
import StepSelectors from './StepSelectors.vue'
import cloneDeep from 'lodash/cloneDeep';
import { WorkflowRun } from './workflow_models';

export default {
    name: 'WorkflowOverview',
    components: {
        StepSelectors,
    },
    mixins: [
        errorHandling,
        currentUserInfoMixin,
    ],
    props: {
        runId: {
            type: String,
            required: true,
        }
    },
    data: function() {
        return {
            errorMsg: 'Error',
            error: false,
            loading: true,
            runData: {},  // from API
            workflow_spec: {},
            tasksById: {},
            selectedStepName: '',  // update from selector
            taskIdFromUrl: null,  // if there is a task ID/PK in the URL querystring, we put it here on mounted()
            isFirstLoad: true,  // boolean to know if it was our first load or not, so we know if we should pay attention to taskIdFromUrl
            // FOR TESTING DIAGRAMS
            //d3Svg: null,  // FOR TESTING D3
        }
    },
    computed: {
        stepsWithTasks() {  // filter any hidden steps, then merge in the Tasks (linked through TaskSpec)
            // we want the actual tasks in there, not really the task specs -- the tasks themselves are what's being managed
            const allSteps = this.workflow_spec.steps ?? [];
            const stepsNotHidden = allSteps.filter(step => !step.is_hidden);
            const stepsWithTasks = stepsNotHidden.map(step => {
                const foundTasks = Object.values(this.tasksById).filter(t => step.id === t.step_id);
                const needsAttention = foundTasks.some(t => (t.needs_attention));  // we set .needs_attention server side
                const hasConcreteTasks = !!foundTasks.filter(t => t.is_concrete_task).length;
                //const tasksComplete = foundTasks.every(t => t.is_completed); // if all of the tasks are completed, the step is complete
                const tasksComplete = foundTasks.every(t => t.workflow_can_proceed);  // this is better to check vs. is_completed -- includes 'skipped' and 'cancelled'
                //const hasTasks = !!foundTasks.length;  
                const isCurrent = step.state_name === this.runData.sm_state;  // current does not necessarily mean 'selected' in the StepSelectors component
                const isCurrentAndFinal = isCurrent && step.is_final_state;
                const isSuccess = (tasksComplete && hasConcreteTasks) || (isCurrentAndFinal);  // only show success if it has tasks (to avoid showing success for 'closed' step too early) OR if its the current state/step, and its a final state (ie: closed)
                const hasOpenTasksForUser = isCurrent && foundTasks.some(t => (!t.is_completed && t.assigned_tus.map(tu => tu.id).includes(this.$currentUserInfo.tenant_user_id)));
                return {...step, tasks: foundTasks, isError: needsAttention, isSuccess, isCurrent, isCurrentAndFinal, showBadge: hasOpenTasksForUser };
            });
            const makeStepHashKey = (stepObj) => {
                // we need something unique that will update if anything in the step changes, to be used as :key in StepSelectors Vue component
                // if anything that relates to display or behavior of this state changes, the key should be different
                // doesn't really matter what it is... just that its different if things change, same if things do not
                // we'll take the stepId + concat all its task.id and task.sm_state -- concat to a big string
                // and add on something for if the state is isError, isCurrent
                const tasksHash = stepObj.tasks.map(t => `${t.id}-${t.sm_state}`).join('-');
                return stepObj.id + tasksHash + (stepObj.isError ? '-e' : '-n') + (stepObj.isCurrent ? '-c' : '-n');
            }
            const withExtraStuff = stepsWithTasks.map((st) => {
                return { ...st, hashKey: makeStepHashKey(st)};  // icon: 'record-fill'
            });
            // NEW - Feb 4 2022 -- we will now filter out any steps that are not final, and also do not have ANY tasks (even template tasks) 
            // because we added a step to workflows after they started, but no tasks got created in those steps, so we'll hide them
            return withExtraStuff.filter(st => (st.tasks.length || st.is_final_state));  // will only return ones with tasks
            //return withExtraStuff;
        },
        stepsByName() {
            return Object.fromEntries(this.stepsWithTasks.map(st => [st.state_name, st]));
        },
        selectedStep() {  // also has all the 'enhanced' stuff on it
            if (!this.selectedStepName) return null;
            return this.stepsByName[this.selectedStepName];
        },
        selectedStepTasks() {
            // This is the final list of tasks we actually display
            // we will filter the list to HIDE (not include) any template_tasks that are parent template of concrete tasks we're showing
            if (!this.selectedStepName) return [];
            const currentStep = this.stepsByName[this.selectedStepName];
            if (!currentStep) return [];
            const allCurrentStepTasks = currentStep.tasks;
            const templateTaskIds = allCurrentStepTasks.map(task => task.template_task_id).filter(ttId => !!ttId);  // filter removes the nulls... probably many of them
            return allCurrentStepTasks.filter(task => !templateTaskIds.includes(task.id));  // remove any template tasks if their concrete tasks were showing
        },
        showSelectorsLoading() {
            // return Boolean, if true, forces StepSelectors into loading state
            // if we have stepsWithTasks already (like if doing a reload) then don't show loading?
            // but if not, and loading = true, then show loading
            const hasSteps = !!this.stepsWithTasks && Array.isArray(this.stepsWithTasks) && this.stepsWithTasks.length;
            return !hasSteps && this.loading;
        },
        closedNote() {
            // get from 'closed_note' from backend, or set a default one here 
            return this.runData.closed_note ? this.runData.closed_note : `This ${this.workflow_spec.name } has been completed, and is now closed.`;
        }
    },
    mounted: function() {
        /* const d3Svg = window.chartSvg;
        if (d3Svg) {
            this.d3Svg = d3Svg;
        } */
        this.getTaskIdFromUrl();  
        this.getDetails({updateStepSelection: true});  // updateStepSelection = true so it auto goes to current step;
    },
    methods: {
        // METHODS YOU CAN CALL FROM PARENT USING $ref
        async reload({updateStepSelection = true}) {
            // just call getDetails again - later you can implement checking for unsaved data, etc.
            return await this.getDetails({updateStepSelection});
        },
        // use this.apiErrorHandler(error, extraMsg, localMsg) from mixin - localMsg is bool, if true puts message in this.errorHandlingMessage
        // NEW FEB 18 2022 - will try to refetch everything on step selection in case workflow was updated remotely
        //buildSteps() {
        //
        //},
        getTaskIdFromUrl() {
            // gets the task specified in URL querystring, if any
            const queryString = window.location.search;
            const urlHash = window.location.hash;  // this will have leading # on it -- but it DOES get returned from server, just like qs, so you could use either
            const urlParams = new URLSearchParams(queryString);
            const taskId = urlParams.get('task');
            console.log('taskId from querystring: ' + taskId);
            console.log('location hash is: ' + urlHash);
            this.taskIdFromUrl = taskId;
        },
        handleApiResponse({ apiData, updateStepSelection = false }) {
            // DRY for handling response from getDetails or updateInstanceData
            // depending on endpoint we might have tasks nested in apiData, or might have 'workflow_run' and 'tasks' separate
            const workflowRunData = apiData.workflow_run ? apiData.workflow_run : apiData;
            const tasksList = apiData.tasks;  // for now, no matter the format, tasks will always be one level deep, under 'tasks' key
            // see comments below for updateStepSelection
            this.metadata = Object.assign({}, workflowRunData.metadata);
            this.workflow_spec = Object.assign({}, workflowRunData.workflow_spec);
            const tasks = cloneDeep(tasksList);
            this.tasksById = Object.fromEntries(tasks.map(t => [t.id, t]));
            const runData = new WorkflowRun(workflowRunData);
            this.runData = Object.assign({}, runData);
            // no return... just set things
            this.$nextTick(() => {
                this.setInitialStepSelection(updateStepSelection);
            });
        },
        async getDetails({ updateStepSelection = true }) {
            this.loading = true;
            try {
                const { data } = await suppliosApi.workflowRun.get_overview(this.runId);
                // data will have { workflow_run, tasks } -- not as much data as workflowRun.get_detail but enough for this component
                this.handleApiResponse({ apiData: data, updateStepSelection });
            } catch (err) {
                this.apiErrorHandler(err);
            }
            this.loading = false; 
        },
        setInitialStepSelection(updateStepSelection) {
            console.log('from setInitialStepSelection');
            console.log('isFirstLoad', this.isFirstLoad);
            console.log('taskIdFromUrl', this.taskIdFromUrl);
            console.log('updateStepSelection', updateStepSelection);
            // updateStepSelection -- if true, we'll ignore any previously set selectedStepName
            // use updateStepSelection = true when you want to go to the currently active step in the workflow
            // use that when you have a state change and you are reloading (but its not the first load)
            // BUT... we ignore it if we know this is the first load (isFirstLoad) AND we have taskId in the URL -- if thats the case, we will go to that step
            const goToTaskIdFromUrl = this.isFirstLoad && !!this.taskIdFromUrl;
            if (goToTaskIdFromUrl) {
                console.log('we should goToTaskIdFromUrl', this.taskIdFromUrl);
                const stepForTaskIdFromUrl = this.getStepForTaskId(this.taskIdFromUrl);
                if (stepForTaskIdFromUrl) {
                    this.selectedStepName = stepForTaskIdFromUrl.state_name;
                    console.log('based on taskIdFromUrl, this is selectedStepName', this.selectedStepName);
                }
            } else if (!this.selectedStepName || updateStepSelection) {
                console.log('no taskIdFromUrl');
                // set it to the current state of the workflow run!
                this.selectedStepName = this.runData?.sm_state ?? '';
                // if workflow has not started yet, it will be in 'draft' mode (or similar) which is likely not visible
                // we'll make sure that step is visible... and if not, we'll pick the first step
                const visibleStepNames = this.stepsWithTasks.map(st => st.state_name); 
                if (visibleStepNames.length && !visibleStepNames.includes(this.selectedStepName)) {
                    this.selectedStepName = visibleStepNames[0];
                }
                console.log('setting this.selectedStepName to', this.selectedStepName);
                // OLD this.selectedStepName = this.stepsWithTasks.length ? this.stepsWithTasks[0].state_name : '';
            }
            this.isFirstLoad = false;  // must set this here, not in earlier functions since this is wrapped in nextTick, if you put in earlier functions it gets set to false before this is called
        },
        getStepForTaskId(taskId) {
            // return the entire step object for a given taskId
            // you don't have step ID on task object... you could add it.  then find the task from this.tasksById, then get stepId... but not totally needed I think
            return this.stepsWithTasks.find(step => (step.tasks.map(st => st.id).includes(taskId)));
        },
        handleStepSelection(stepObject) {
            // will get entire step object - pick off the name
            this.selectedStepName = stepObject.state_name;
            this.reload({updateStepSelection: false});  // TESTING - do NOT update the step selection when we get API response... because we just wanted to change it
        },
        handleTaskStateChanged(newTaskData) {
            // new task object will be passed in, but for now we'll just reload everything
            // could be called from a step getting completed, admin taking some action, etc.
            console.log(newTaskData);
            this.getDetails({updateStepSelection: true});  // updateStepSelection = true so it updates to changed step if needed
        },
    }
}
</script>

<style>

</style>